import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { delay, dematerialize, materialize, mergeMap } from 'rxjs/operators';
import {
    AsioProductTypeId,
    CloudManagerAdvancedPermissionsFeatureLicenseId,
    CloudManagerProductTypeId,
    CloudManagerUserLicenseId,
} from 'src/app/pci/licensing/cloud-manager-product-catalog-ids';

import { AbstractUserProvider } from '@sharedservices/identity-auth-angular';

@Injectable()
export class LocalPlatformServicesStub implements HttpInterceptor {
    /** Toggle to simulate ASIO license for local testing of redirection flows. */
    private static readonly SIMULATE_ASIO_LICENSE = false;

    private readonly user = this.userProvider.getCurrentUser();
    constructor(private readonly userProvider: AbstractUserProvider) {
    }

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        /* simulate call to server */
        return of(null)
            .pipe(
                mergeMap(() => this.handleRoute(req, next)),
                materialize(),
                delay(500),
                dematerialize()
            );
    }

    private handleRoute(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const { url } = req;

        switch (true) {
            case url.includes(`/licenseassignment/api/v1/licenseassignment/partner/${this.user.partnerId}/user/${this.user.userId}`):
                return this.ok({
                    "userAssignments": [
                        {
                            "partnerId": this.user.partnerId,
                            "userId": this.user.userId,
                            "productId": CloudManagerProductTypeId,
                            "licenseTypeId": CloudManagerUserLicenseId,
                            "assignPermissions": true
                        }
                    ],
                    "featureAssignments": [
                        {
                            "partnerId": this.user.partnerId,
                            "productId": CloudManagerProductTypeId,
                            "licenseTypeId": CloudManagerAdvancedPermissionsFeatureLicenseId
                        }
                    ]
                });
            case url.includes('/subscriptionmanagement/api/v1/SubscriptionManagement'):
                return this.ok(this.getSubscriptions());
            case this.getPipeRoute('/papi/banners').includes(url):
                return this.ok([{ 'enabled': true, 'type': 'test', 'message': 'This is a test banner. You can close me and it will persist in LocalStorage' }]);
            case this.getPipeRoute('/papi/partner/logo').includes(url):
                return this.ok({});
            case this.getPipeRoute('/papi/partner/admin/24114725-9425-e311-a540-b4b52f67d6de/authenticationsettings').includes(url):
                return this.ok({ authenticationType: 0 });
            case this.getPipeRoute('/papi/usermanagement/request-mfa-activation').includes(url):
                return this.ok({});
            default:
                return next.handle(req);
        }
    }

    private getPipeRoute(path: string): string[] {
        return [
            `https://route.skykick.dev/pipe${path}`,
            `https://nonprod-routing.skykick.dev/pipe${path}`,
            `https://shared-dev-routing.cloudservices.connectwise.dev/pipe${path}`
        ];
    }

    private ok<T>(body?: T): Observable<HttpResponse<T>> {
        return of(new HttpResponse<T>({ body, status: 200 }));
    }

    private getSubscriptions(): object[] {
        const subscriptions = [
            {
                id: 'local-cm-subscription',
                externalId: '',
                productCatalogId: 'a32d67e7-e4ba-4a40-92d7-80c145821c85',
                catalogItems: [],
                productTypeId: CloudManagerProductTypeId,
                partnerId: this.user.partnerId,
                userId: this.user.userId,
                contactEmail: '',
                trial: false,
                status: 'Active',
                currency: 'USD',
                billingFrequency: 'Monthly',
                planType: 'Pro',
                startDate: new Date().toISOString(),
                nextChargeDate: new Date(new Date().getFullYear() + 1, 0).toISOString(),
                termsAccepted: [],
            },
        ];

        if (LocalPlatformServicesStub.SIMULATE_ASIO_LICENSE) {
            subscriptions.push({
                id: 'local-asio-subscription',
                externalId: '',
                productCatalogId: '8723cafb-f8a6-4ac1-9c34-9383ddc68a27',
                catalogItems: [],
                productTypeId: AsioProductTypeId,
                partnerId: this.user.partnerId,
                userId: this.user.userId,
                contactEmail: '',
                trial: false,
                status: 'Active',
                currency: 'USD',
                billingFrequency: 'Monthly',
                planType: 'Pro',
                startDate: new Date().toISOString(),
                nextChargeDate: new Date(new Date().getFullYear() + 1, 0).toISOString(),
                termsAccepted: [],
            });
        }

        return subscriptions;
    }
}

export const localPlatformServicesStubProvider = { provide: HTTP_INTERCEPTORS, useClass: LocalPlatformServicesStub, multi: true };
